import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
} from '@angular/core';
import { GhostPracticeCode } from '@models/ghost-practice';
import { dispatch, select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { getGhostPracticeCodeLabelDescription } from 'src/app/shared/core/util/accounting/ghost-practice';
import { MatterStatementOfAccount } from '@models/statementOfAccount';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { GhostPracticeItem, NotApplicableValue } from '../../accounting.models';
import { SetStatement } from 'src/app/shared/state/matter/statementOfAccounts/statementOfAccounts.actions';

@Component({
	selector: 'app-ghost-practice-accounting-items-table',
	templateUrl: './ghost-practice-accounting-items-table.component.html',
	styleUrls: ['./ghost-practice-accounting-items-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class GhostPracticeAccountingItemsTableComponent {
	private readonly setStatement = dispatch(SetStatement);
	private analyticsService = inject(AnalyticsService);

	public displayedColumns: string[] = ['title', 'amount', 'ghostPracticeCode'];

	ghostPracticeCodes = select(BusinessState.getGhostPracticeCodes);
	ghostPracticeHSTDisbursementCodes = select(
		BusinessState.getGhostPracticeHSTDisbursementCodes,
	);
	ghostPracticeNonHSTDisbursementCodes = select(
		BusinessState.getGhostPracticeNonHSTDisbursementCodes,
	);

	items = input<GhostPracticeItem[]>([]);
	title = input<string>('');
	hst = input<number>(null);
	hstTotal = input<number>(null);
	codeColumnTitle = input<string>('G/L Account');

	codeDropdownOptions = computed(() => {
		const codes = this.hst()
			? this.ghostPracticeHSTDisbursementCodes()
			: this.ghostPracticeNonHSTDisbursementCodes();

		const options = codes.map((code: GhostPracticeCode) => ({
			label: getGhostPracticeCodeLabelDescription(code),
			value: code,
		}));

		options.unshift({
			label: 'N/A',
			value: {
				id: NotApplicableValue,
				description: NotApplicableValue,
				glAccount: '',
				taxApplies: false,
			} as GhostPracticeCode,
		});

		return options;
	});

	total = computed(() => {
		return this.items().reduce(
			(acc, item) => acc + item.accountingItem.amount,
			0,
		);
	});

	grandTotal = computed(() => {
		return this.total() + this.hstTotal();
	});

	onCodeChange(event: GhostPracticeCode, item: GhostPracticeItem) {
		this.analyticsService.logEvent(
			AnalyticsEvent.GHOST_PRACTICE_FILE_ADDED_CODE,
		);

		const result: MatterStatementOfAccount = { ...item.statement };

		if (event) {
			result.ghostPracticeCode = event;

			this.setStatement({
				id: item.statement.id,
				statement: result,
			});
		}
	}

	getBackupValue(code: GhostPracticeCode): string {
		return getGhostPracticeCodeLabelDescription(code);
	}

	trackByFn(_: number, item: GhostPracticeItem) {
		return item.accountingItem.statementId;
	}
}
